<template>
  <portal :to="portalTarget">
    <transition
      name="modal"
      appear
      @enter="focusModal"
      @after-enter="focusModal">
      <div
        v-if="isOpen"
        ref="backdrop"
        class="c-modal__backdrop"
        @click="isOpen = false"
        @keydown.tab="trapTab"
        @keydown.esc="isOpen = false">
        <button
          v-if="image || fullscreen"
          ref="closeButton"
          class="o-unstyled c-modal__iconClose o-flex -alignCenter -center"
          :class="{
            '-fullscreen': fullscreen,
          }"
          :aria-label="$t('generic.close')"
          @click="isOpen = false">
          <template v-if="fullscreen">
            {{ $t('generic.close') }}
          </template>
          <SvgIcon
            type="close"
            medium />
        </button>
        <div
          class="c-modal__wrapper"
          :class="{
            '-image': image,
            '-fullscreen': fullscreen,
          }"
          data-cy="GenericModal__window">
          <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
          <div
            ref="modalContent"
            role="dialog"
            aria-modal="true"
            :aria-label="$t('generic.modal')"
            class="c-modal"
            :class="{
              '-large': large,
              '-wide': wide,
              '-mediumWide': mediumWide,
              '-scroll': scroll,
              '-image': image,
              '-fullscreen': fullscreen,
            }"
            tabindex="-1"
            @click="handleModalClick">
            <slot />
          </div>
        </div>
      </div>
    </transition>
  </portal>
</template>

<script>
import { useEventListener } from '@vueuse/core'

const openModals = []

export default {
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    large: {
      type: Boolean,
      default: false,
    },
    wide: {
      type: Boolean,
      default: false,
    },
    mediumWide: {
      type: Boolean,
      default: false,
    },
    scroll: {
      type: Boolean,
      default: false,
    },
    image: {
      type: Boolean,
      default: false,
    },
    fullscreen: {
      type: Boolean,
      default: false,
    },
    portalTarget: {
      type: String,
      default: 'modal',
    },
  },
  data() {
    return {
      previousActiveElement: null,
    }
  },
  mounted() {
    this.stopFocusListener = useEventListener(document, 'focusin', (event) => {
      if (this.isOpen && openModals[openModals.length - 1] === this && this.$refs.backdrop && !this.$refs.backdrop.contains(event.target)) {
        this.focusModal()
      }
    })
  },
  beforeDestroy() {
    this.stopFocusListener?.()
    this.restoreFocus()
  },
  computed: {
    isOpen: {
      set(isOpen) {
        this.$emit('input', isOpen)
      },
      get() {
        return this.value
      },
    },
  },
  watch: {
    isOpen: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.previousActiveElement = document.activeElement
          openModals.push(this)
        } else {
          this.restoreFocus()
        }
      },
    },
  },
  methods: {
    focusModal() {
      if (this.isOpen && openModals[openModals.length - 1] === this && !this.$refs.backdrop?.contains(document.activeElement)) {
        this.$refs.modalContent?.focus()
      }
    },
    restoreFocus() {
      const index = openModals.indexOf(this)
      if (index !== -1) {
        openModals.splice(index, 1)
      }
      const previous = this.previousActiveElement
      this.previousActiveElement = null
      if (previous?.isConnected && previous.getClientRects().length) {
        previous.focus()
      }
    },
    trapTab(event) {
      if (openModals[openModals.length - 1] !== this) {
        return
      }
      const focusable = [...this.$refs.backdrop.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')]
        .filter((element) => !element.disabled && element.tabIndex >= 0 && element.getClientRects().length && !element.closest('[inert]'))
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement
      if (!first) {
        event.preventDefault()
        this.focusModal()
      } else if (event.shiftKey && (active === first || !focusable.includes(active))) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && (active === last || !focusable.includes(active))) {
        event.preventDefault()
        first.focus()
      }
    },
    handleModalClick($event) {
      $event.stopPropagation()
    },
  },
}
</script>
